import { BaseStore } from "$lib/stores/base.svelte";
import { api } from "$lib/helpers/api.service";
import { cache } from "$lib/helpers/cache.service";
import { filtersStore } from "$lib/stores/filters.svelte";
import type { PoiResponse, AreaBounds, PoiItem } from "$lib/types";
import { PlantStatus } from "$lib/types/poi.types";

interface GeoJSONFeature {
  type: "Feature";
  geometry: {
    type: "Point";
    coordinates: [number, number];
  };
  properties: any;
}

interface GeoJSONFeatureCollection {
  type: "FeatureCollection";
  features: GeoJSONFeature[];
}

class PoiStore extends BaseStore {
  private static readonly CACHE_NAME = "poi-coordinates";

  private visibleBounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null = null;
  private currentPrecision = 0.01;

  public loadedPois = $state<Map<string, PoiItem>>(new Map());
  private additionalFeatures: Map<string, GeoJSONFeature> = new Map();
  private updateCallbacks: Set<(features: GeoJSONFeature[]) => void> =
    new Set();

  constructor() {
    super();
    cache.createCache(PoiStore.CACHE_NAME);
  }

  // Generate a hash based on active filters to include in the cache key
  private get activeFilterHash(): string {
    return filtersStore.getActiveSubcategories().sort().join(",");
  }

  setVisibleBounds(bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) {
    this.visibleBounds = bounds;
    this.notifySubscribers();
  }

  subscribeToMapLibre(callback: (features: GeoJSONFeature[]) => void) {
    this.updateCallbacks.add(callback);

    if (this.loadedPois.size > 0 || this.additionalFeatures.size > 0) {
      try {
        callback(this.getGeoJSONFeatures());
      } catch (error) {
        console.error("Error in initial callback:", error);
      }
    }

    return () => {
      this.updateCallbacks.delete(callback);
    };
  }

  private notifySubscribers() {
    const features = this.getGeoJSONFeatures();
    this.updateCallbacks.forEach((callback) => {
      try {
        callback(features);
      } catch (error) {
        console.error("Error in subscriber callback:", error);
      }
    });
  }

  private getGeoJSONFeatures(): GeoJSONFeature[] {
    const bounds = this.visibleBounds
      ? this.padBounds(this.visibleBounds, 0.15)
      : null;

    const poiFeatures = Array.from(this.loadedPois.values())
      .filter((poi) => (bounds ? this.isInBounds(poi, bounds) : true))
      .filter((poi) => this.isPoiVisible(poi))
      .map((poi) => this.poiToGeoJSON(poi));

    const additionalFeatures = Array.from(this.additionalFeatures.values());

    return [...poiFeatures, ...additionalFeatures];
  }

  private poiToGeoJSON(poi: PoiItem): GeoJSONFeature {
    return {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [poi.longitude, poi.latitude],
      },
      properties: {
        id: poi.id,
        name: poi.name ?? null,
        address: poi.address ?? null,
        label: poi.label ?? null,
        subCategory: poi.subCategory ?? null,
        hasCoupon: poi.hasCoupon ?? false,
        // Convert enum to string and handle 0 vs null/undefined safely
        waterableStatus:
          poi.waterableStatus !== undefined && poi.waterableStatus !== null
            ? PlantStatus[poi.waterableStatus]
            : null,
        latitude: poi.latitude,
        longitude: poi.longitude,
      },
    };
  }

  private readonly REQUEST_TILE_SIZE = 0.01;

  async loadPoisForMapCoordinates(
    neLat: number,
    neLong: number,
    swLat: number,
    swLong: number,
    zoom = 12
  ) {
    this.currentPrecision = this.getPrecisionForZoom(zoom);
    const filterHash = this.activeFilterHash;
    const subCategoriesParam = filterHash;

    const tilesToLoad = this.calculateTilesToLoad(
      neLat,
      neLong,
      swLat,
      swLong,
      this.currentPrecision,
      filterHash
    );

    if (tilesToLoad.length === 0) return;

    // Use a maximum concurrency for requests
    const CONCURRENCY_LIMIT = 4;
    for (let i = 0; i < tilesToLoad.length; i += CONCURRENCY_LIMIT) {
      const batch = tilesToLoad.slice(i, i + CONCURRENCY_LIMIT);

      await Promise.all(
        batch.map(async (tile) => {
          const pois = await this.executeWithLoading(
            () =>
              api.get<PoiResponse>("/point-of-interests/recommended", {
                neLat: tile.north,
                neLng: tile.east,
                swLat: tile.south,
                swLng: tile.west,
                subCategories:
                  (subCategoriesParam.length > 0 &&
                    filtersStore.activeFilter) ||
                  filtersStore.activeCategory
                    ? subCategoriesParam
                    : undefined,
              }),
            "Failed to load POIs"
          );

          if (pois) {
            this.processNewPois(pois);
            this.markTileAsLoaded(tile, this.currentPrecision, filterHash);
          }
        })
      );
    }

    this.isLoading = false;
    this.notifySubscribers();
  }

  private calculateTilesToLoad(
    neLat: number,
    neLong: number,
    swLat: number,
    swLong: number,
    precision: number,
    filterHash: string
  ): { north: number; south: number; east: number; west: number }[] {
    const tiles: {
      north: number;
      south: number;
      east: number;
      west: number;
    }[] = [];

    // Round bounds to align with tile grid
    const startLat =
      Math.floor(swLat / this.REQUEST_TILE_SIZE) * this.REQUEST_TILE_SIZE;
    const endLat =
      Math.ceil(neLat / this.REQUEST_TILE_SIZE) * this.REQUEST_TILE_SIZE;
    const startLng =
      Math.floor(swLong / this.REQUEST_TILE_SIZE) * this.REQUEST_TILE_SIZE;
    const endLng =
      Math.ceil(neLong / this.REQUEST_TILE_SIZE) * this.REQUEST_TILE_SIZE;

    // Epsilon for float comparison
    const epsilon = this.REQUEST_TILE_SIZE / 1000;

    for (
      let lat = startLat;
      lat < endLat - epsilon;
      lat += this.REQUEST_TILE_SIZE
    ) {
      for (
        let lng = startLng;
        lng < endLng - epsilon;
        lng += this.REQUEST_TILE_SIZE
      ) {
        // Define the bounds of this tile
        const tileNorth = lat + this.REQUEST_TILE_SIZE;
        const tileEast = lng + this.REQUEST_TILE_SIZE;

        // Optimize: Check if *any* point in this tile is missing from cache
        // We check the fine-grained grid points within this tile
        if (
          this.isTileMissingData(
            lat,
            lng,
            tileNorth,
            tileEast,
            precision,
            filterHash
          )
        ) {
          tiles.push({
            north: tileNorth,
            south: lat,
            east: tileEast,
            west: lng,
          });
        }
      }
    }

    return tiles;
  }

  private isTileMissingData(
    south: number,
    west: number,
    north: number,
    east: number,
    precision: number,
    filterHash: string
  ): boolean {
    const epsilon = precision / 1000;

    // We sample points within the tile.
    // If we find missing cache entries, we consider the tile needs reloading.
    // Optimization: we don't need to check EVERY point, but checking the grid is safer.

    for (let lat = south; lat <= north + epsilon; lat += precision) {
      for (let lng = west; lng <= east + epsilon; lng += precision) {
        const key = this.getCacheKeyWithStep(lat, lng, precision, filterHash);
        if (!cache.has(PoiStore.CACHE_NAME, key)) {
          return true;
        }
      }
    }
    return false;
  }

  private markTileAsLoaded(
    tile: { north: number; south: number; east: number; west: number },
    precision: number,
    filterHash: string
  ) {
    const epsilon = precision / 1000;

    for (let lat = tile.south; lat <= tile.north + epsilon; lat += precision) {
      for (let lng = tile.west; lng <= tile.east + epsilon; lng += precision) {
        const key = this.getCacheKeyWithStep(lat, lng, precision, filterHash);
        cache.set(PoiStore.CACHE_NAME, key, true);
      }
    }
  }

  private isInBounds(
    poi: PoiItem,
    bounds: { north: number; south: number; east: number; west: number }
  ): boolean {
    const { latitude: lat, longitude: lng } = poi;
    return (
      lat <= bounds.north &&
      lat >= bounds.south &&
      lng <= bounds.east &&
      lng >= bounds.west
    );
  }

  private padBounds(
    b: { north: number; south: number; east: number; west: number },
    ratio: number
  ) {
    const latPad = (b.north - b.south) * ratio;
    const lngPad = (b.east - b.west) * ratio;
    return {
      north: b.north + latPad,
      south: b.south - latPad,
      east: b.east + lngPad,
      west: b.west - lngPad,
    };
  }

  private refetchTimeout: NodeJS.Timeout | null = null;

  private getZoomFromPrecision(precision: number): number {
    if (precision >= 0.25) return 5;
    if (precision >= 0.1) return 7;
    if (precision >= 0.05) return 9;
    if (precision >= 0.01) return 11;
    if (precision >= 0.005) return 13;
    if (precision >= 0.002) return 15;
    return 16;
  }

  applyFilters(refetch = true) {
    // Notify subscribers first so client-side filtering happens immediately
    this.notifySubscribers();

    if (refetch && this.visibleBounds) {
      if (this.refetchTimeout) clearTimeout(this.refetchTimeout);
      this.refetchTimeout = setTimeout(() => {
        const { north, east, south, west } = this.visibleBounds!;
        const zoom = this.getZoomFromPrecision(this.currentPrecision);
        this.loadPoisForMapCoordinates(north, east, south, west, zoom);
      }, 300);
    }
  }

  private processNewPois(pois: PoiResponse) {
    const newMap = new Map(this.loadedPois);
    let changed = false;
    pois.items.forEach((poi) => {
      if (!newMap.has(poi.id)) {
        newMap.set(poi.id, poi);
        changed = true;
      }
    });
    if (changed) {
      this.loadedPois = newMap;
    }
  }

  private isPoiVisible(poi: PoiItem): boolean {
    if (filtersStore.couponFilter) {
      return poi.hasCoupon;
    }

    const category = filtersStore.filters[poi.label];
    if (!category) return false;

    const sub = category.subcategories[poi.subCategory];
    if (!sub) return false;

    return sub.enabled;
  }

  private getCacheKeyWithStep(
    lat: number,
    long: number,
    step: number,
    filterHash: string
  ): string {
    const latStr = this.getRoundedCoordinate(lat, step).toFixed(4);
    const lngStr = this.getRoundedCoordinate(long, step).toFixed(4);
    return `${step}:${latStr}_${lngStr}:${filterHash}`;
  }

  private getRoundedCoordinate(coordinate: number, step: number): number {
    const multiplier = 1 / step;
    return Math.round(coordinate * multiplier) / multiplier;
  }

  private getPrecisionForZoom(zoom: number): number {
    if (zoom <= 5) return 0.25;
    if (zoom <= 7) return 0.1;
    if (zoom <= 9) return 0.05;
    if (zoom <= 11) return 0.01;
    if (zoom <= 13) return 0.005;
    if (zoom <= 15) return 0.002;
    return 0.001;
  }
}

export const poiStore = new PoiStore();
